import { useState, useEffect } from "react";
import { FEATURES_CSS } from "./FeaturesPage.jsx";
import { PRICING, PLAN_KEYS, PLAN_PRICES, ANNUAL_MONTHS } from "../content/pricingContent.js";
import { getLocale as i18nGetLocale } from "../lib/i18n.js";

// Public /pricing and /en/pricing — the plan page for shop owners. Same look
// as /features (shares its CSS). The crawler version is api/features-og.js
// (page=pricing), built from the same content module.

// /en/pricing is English; bare /pricing follows the visitor's app language
// (crawlers keep the Chinese page, like /features).
const langFromPath = () => {
  try {
    if (/^\/en\/pricing/.test(window.location.pathname)) return "en";
    if (/bot|crawl|spider|slurp|lighthouse/i.test(navigator.userAgent || "")) return "zh";
    return (i18nGetLocale() || "zh-HK").startsWith("zh") ? "zh" : "en";
  } catch { return "zh"; }
};

const CSS = `
.pbp-toggle { display:inline-flex; background:#F3EEF6; border-radius:99px; padding:4px; gap:4px; margin:6px 0 22px; }
.pbp-toggle button { border:none; background:none; border-radius:99px; padding:8px 16px; font-weight:900; font-size:14px; color:var(--soft); cursor:pointer; }
.pbp-toggle button.on { background:#fff; color:var(--ink); box-shadow:0 2px 8px rgba(0,0,0,.08); }
.pbp-toggle small { color:#16A34A; font-weight:900; margin-left:4px; }
.pbp-trial { background:linear-gradient(135deg,#F5F3FF,#EDE9FE); border:2px solid #C4B5FD; color:#5B21B6; border-radius:16px; padding:12px 16px; font-weight:900; font-size:14px; margin:0 0 22px; }
.pbp-card { display:flex; flex-direction:column; }
.pbp-card .tag { font-size:13px; font-weight:700; color:var(--soft); min-height:36px; margin:0 0 8px; }
.pbp-card .usd { font-size:13px; color:var(--soft); font-weight:800; }
.pbp-card .yr { font-size:12px; color:var(--soft); font-weight:700; margin-top:2px; min-height:16px; }
.pbp-card .pop { align-self:flex-start; background:var(--pink); color:#fff; font-size:11px; font-weight:900; border-radius:99px; padding:3px 10px; margin-bottom:8px; }
.pbp-card .go { margin-top:auto; width:100%; white-space:normal; line-height:1.3; }
.pbp-card .yr { margin-bottom:14px; }
.pbp-plans { display:grid; grid-template-columns:repeat(auto-fit, minmax(220px, 1fr)); gap:14px; }
.pbp-card { min-width:0; }
.pbp-table { min-width:560px; }
.pbp-table { width:100%; border-collapse:collapse; font-size:14px; background:#fff; border:1px solid var(--line); border-radius:16px; overflow:hidden; }
.pbp-table th, .pbp-table td { padding:10px 8px; border-bottom:1px solid var(--line); text-align:center; }
.pbp-table th:first-child, .pbp-table td:first-child { text-align:left; font-weight:800; color:var(--ink); }
.pbp-table thead th { background:#FFF4F7; font-weight:900; }
.pbp-table .hl { background:#FFF9FB; }
.pbp-yes { color:#16A34A; font-weight:900; }
.pbp-no { color:#C9C4D6; }
.pbp-scroll { overflow-x:auto; -webkit-overflow-scrolling:touch; }
.pbp-faq h3 { font-size:16px; margin:18px 0 4px; }
.pbp-faq p { margin:0; color:var(--soft); font-weight:700; line-height:1.7; }
`;

export default function PricingPage({ onSignup, onHome }) {
  const [lang, setLang] = useState(langFromPath);
  const [yearly, setYearly] = useState(false);
  const P = PRICING[lang === "en" ? "en" : "zh"];
  useEffect(() => {
    try { document.title = P.seo.title; document.documentElement.lang = P.lang; } catch { /* ignore */ }
  }, [P]);
  useEffect(() => {
    const onPop = () => setLang(langFromPath());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const switchLang = (e) => {
    e.preventDefault();
    const to = lang === "en" ? "zh" : "en";
    try { window.history.pushState({}, "", PRICING[to].path); } catch { /* ignore */ }
    setLang(to);
  };
  const signup = (e) => { e?.preventDefault?.(); onSignup?.(); };
  const cell = (v) => v === true ? <span className="pbp-yes">✓</span> : v === false ? <span className="pbp-no">—</span> : <span style={{ fontWeight: 800 }}>{v}</span>;
  const featuresHref = lang === "en" ? "/en/features" : "/features";
  const helpHref = lang === "en" ? "/en/help" : "/help";

  return (
    <div className="pbf" lang={P.lang}>
      <style>{FEATURES_CSS + CSS}</style>
      <nav className="pbf-nav">
        <div className="pbf-wrap">
          <a className="pbf-logo" href="/" onClick={(e) => { if (onHome) { e.preventDefault(); onHome(); } }}>🐾 PettoBook</a>
          <div className="pbf-navlinks">
            <a href={featuresHref}>{lang === "en" ? "Features" : "功能介紹"}</a>
            <a href={helpHref}>{lang === "en" ? "Help" : "使用教學"}</a>
            <a href="/directory">{lang === "en" ? "Groomer directory" : "美容店目錄"}</a>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <a className="pbf-tab" style={{ border: "1px solid var(--line)" }} hrefLang={lang === "en" ? "zh-HK" : "en"} href={P.other[0]} onClick={switchLang}>{P.other[1]}</a>
            <button className="pbf-btn pri sm" onClick={signup}>{P.ctaFree}</button>
          </div>
        </div>
      </nav>

      <section className="pbf-wrap" style={{ padding: "36px 16px 10px" }}>
        <h1 style={{ fontSize: 34, fontWeight: 900, margin: "0 0 8px" }}>{P.title}</h1>
        <p style={{ fontSize: 17, color: "var(--soft)", fontWeight: 700, margin: "0 0 18px", maxWidth: 680 }}>{P.lead}</p>
        <div className="pbp-trial">{P.trial}</div>
        <div className="pbp-toggle" role="tablist">
          <button className={yearly ? "" : "on"} onClick={() => setYearly(false)}>{P.monthly}</button>
          <button className={yearly ? "on" : ""} onClick={() => setYearly(true)}>{P.yearly}<small>{P.yearlySave}</small></button>
        </div>

        <div className="pbp-plans">
          {PLAN_KEYS.map((k) => {
            const pop = k === "pro";
            const m = PLAN_PRICES[k];
            const shown = yearly && m ? Math.round(m * ANNUAL_MONTHS / 12) : m;
            return (
              <div key={k} className={"pbf-plan pbp-card" + (pop ? " hl" : "")}>
                {pop && <span className="pop">{P.popular}</span>}
                <h4>{P.names[k]}</h4>
                <p className="tag">{P.taglines[k]}</p>
                <div className="pr">{m ? `HK$${shown}` : P.price(k)}{m ? <small>{P.perMonth}</small> : null}</div>
                {P.usd && m ? <div className="usd">{lang === "en" ? `≈ US$${Math.round(shown / 7.8)}` : ""}{P.perMonth}</div> : null}
                <div className="yr">{yearly && m ? P.billedYearly(m * ANNUAL_MONTHS) : ""}</div>
                <button className={"pbf-btn go " + (pop ? "pri" : "sec")} onClick={signup}>{k === "free" ? P.ctaFree : P.cta}</button>
              </div>
            );
          })}
        </div>
      </section>

      <section className="pbf-wrap" style={{ padding: "26px 16px 8px" }}>
        <h2 style={{ fontSize: 24, fontWeight: 900 }}>{P.compareTitle}</h2>
        <div className="pbp-scroll">
          <table className="pbp-table">
            <thead>
              <tr><th>{P.feature}</th>{PLAN_KEYS.map((k) => <th key={k} className={k === "pro" ? "hl" : ""}>{P.names[k]}</th>)}</tr>
            </thead>
            <tbody>
              {P.rows.map(([label, ...vals]) => (
                <tr key={label}><td>{label}</td>{vals.map((v, i) => <td key={i} className={PLAN_KEYS[i] === "pro" ? "hl" : ""}>{cell(v)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: 12.5, color: "var(--soft)", fontWeight: 700 }}>{P.note}</p>
      </section>

      <section className="pbf-wrap pbp-faq" style={{ padding: "18px 16px 30px" }}>
        <h2 style={{ fontSize: 24, fontWeight: 900 }}>{P.faqTitle}</h2>
        {P.faq.map(([q, a]) => <div key={q}><h3>{q}</h3><p>{a}</p></div>)}
        <div className="pbf-ctas" style={{ marginTop: 24 }}>
          <button className="pbf-btn pri" onClick={signup}>{P.cta}</button>
          <a className="pbf-btn sec" href={featuresHref}>{lang === "en" ? "See all features" : "睇全部功能"}</a>
        </div>
      </section>

      <footer className="pbf-foot">
        {P.links.map(([href, label], i) => <span key={href}>{i > 0 && "·"}<a href={href}>{label}</a></span>)}
        <div style={{ marginTop: 10 }}>© PettoBook</div>
      </footer>
    </div>
  );
}
